import React, { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { ProviderSettingsSkeleton } from "../../components/skeleton/PageSkeletons";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAccess } from "../../components/AccessContext";
import "./AIProviderSettings.css";
import "./SmtpSettings.css";

const encryptionOptions = [
  { id: "tls", label: "TLS (STARTTLS)", hint: "Recommended · usually port 587", port: 587 },
  { id: "ssl", label: "SSL", hint: "Usually port 465", port: 465 },
  { id: "none", label: "None", hint: "Not recommended · usually port 25", port: 25 }
];
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const hostPattern = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*$/;

const toForm = (smtp) => ({
  host: smtp?.settings?.host || "",
  port: smtp?.settings?.port ? String(smtp.settings.port) : "",
  encryption: smtp?.settings?.encryption || "tls",
  username: smtp?.settings?.username || "",
  password: "",
  fromEmail: smtp?.settings?.fromEmail || "",
  fromName: smtp?.settings?.fromName || "",
  enabled: Boolean(smtp?.enabled)
});

const formatDate = (value) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "";

// Mirrors the API's rules so most mistakes are caught before a request. The API remains the authority.
const validate = (form, smtp, { requireComplete }) => {
  const errors = {};
  const host = form.host.trim();
  const port = form.port.trim();
  const saved = smtp?.settings || {};
  if (host && /^[a-z][a-z0-9+.-]*:\/\//i.test(host)) errors.host = "Enter the host name only, without smtp:// or https://.";
  else if (host && !hostPattern.test(host)) errors.host = "Enter a valid SMTP host, for example smtp.example.com.";
  else if (!host && requireComplete) errors.host = "SMTP host is required.";
  if (port && !(/^\d+$/.test(port) && Number(port) >= 1 && Number(port) <= 65535)) errors.port = "Port must be a whole number between 1 and 65535.";
  else if (!port && requireComplete) errors.port = "Port is required.";
  else if (form.encryption === "ssl" && port === "587") errors.port = "Port 587 uses TLS. Choose TLS, or use port 465 for SSL.";
  else if (form.encryption === "tls" && port === "465") errors.port = "Port 465 uses SSL. Choose SSL, or use port 587 for TLS.";
  const username = form.username.trim();
  if (/\s/.test(username)) errors.username = "Username can't contain spaces.";
  const hasPassword = Boolean(form.password) || Boolean(smtp?.passwordSaved);
  const passwordMovesServer = smtp?.passwordSaved && !form.password && username &&
    (host.toLowerCase() !== (saved.host || "") || username !== (saved.username || "") || (form.encryption === "none" && saved.encryption !== "none"));
  if (passwordMovesServer) errors.password = "Re-enter the password to use it with this host, username or encryption.";
  else if (username && !hasPassword) errors.password = "Enter the password for this username.";
  if (form.password && !username) errors.username = "Enter the username for this password, or leave both blank.";
  const fromEmail = form.fromEmail.trim();
  if (fromEmail && !emailPattern.test(fromEmail)) errors.fromEmail = "Enter a valid sender email address.";
  else if (!fromEmail && requireComplete) errors.fromEmail = "Sender email is required.";
  return errors;
};

const toPayload = (form) => ({
  host: form.host.trim(),
  port: form.port.trim() ? Number(form.port) : null,
  encryption: form.encryption,
  username: form.username.trim(),
  ...(form.password ? { password: form.password } : {}),
  fromEmail: form.fromEmail.trim(),
  fromName: form.fromName.trim()
});

// Wires the label, error and hint to the single input passed as children.
const Field = ({ id, label, error, hint, children }) => {
  const messageId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return <div className="admin-ai-credential-field admin-smtp-field">
    <label htmlFor={id}>{label}</label>
    {React.cloneElement(children, { id, "aria-invalid": error ? true : undefined, "aria-describedby": messageId })}
    {error ? <small id={messageId} className="admin-smtp-field-error"><i className="ti ti-alert-circle" aria-hidden="true" />{error}</small> : hint ? <small id={messageId} className="admin-smtp-field-hint">{hint}</small> : null}
  </div>;
};

const SmtpSettings = () => {
  const { can } = useAccess();
  const canManage = can("admin.smtp.manage");
  const [smtp, setSmtp] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [errors, setErrors] = useState({});
  const [recipient, setRecipient] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pageError, setPageError] = useState("");

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const result = await apiRequest("/api/admin/smtp");
      setSmtp(result.smtp);
      setForm(toForm(result.smtp));
      setErrors({});
      setRecipient((current) => current || result.adminEmail || "");
    } catch (error) {
      setPageError(error.message || "Unable to load SMTP settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const update = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      // Follow the encryption choice with its standard port, unless a custom port was entered.
      if (field === "encryption") {
        const standardPorts = encryptionOptions.map((option) => String(option.port));
        if (!current.port || standardPorts.includes(current.port)) next.port = String(encryptionOptions.find((option) => option.id === value).port);
      }
      return next;
    });
    setErrors((current) => ({ ...current, [field]: undefined, ...(field === "encryption" ? { port: undefined } : {}) }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    const nextErrors = validate(form, smtp, { requireComplete: form.enabled });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      showError("Fix the highlighted fields before saving.");
      return;
    }
    setSaving(true);
    try {
      const result = await apiRequest("/api/admin/smtp", { method: "PUT", body: JSON.stringify({ ...toPayload(form), enabled: form.enabled }) });
      setSmtp(result.smtp);
      setForm(toForm(result.smtp));
      showSuccess(result.message || "SMTP settings saved securely.");
    } catch (error) {
      showError(error.message || "Unable to save SMTP settings.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    const nextErrors = validate(form, smtp, { requireComplete: true });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      showError("Complete the highlighted fields before sending a test email.");
      return;
    }
    if (!emailPattern.test(recipient.trim())) {
      setTestResult({ success: false, message: "Enter a valid recipient email address." });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiRequest("/api/admin/smtp/test", { method: "POST", body: JSON.stringify({ to: recipient.trim(), settings: toPayload(form) }) });
      setTestResult({ success: true, message: result.message });
      if (result.smtp) setSmtp(result.smtp);
      showSuccess("Test email sent.");
    } catch (error) {
      setTestResult({ success: false, message: error.message || "The test email couldn't be sent." });
      if (error.data?.smtp) setSmtp(error.data.smtp);
      showError("Test email failed.");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <main className="workspace-dashboard admin-ai-settings admin-smtp"><ProviderSettingsSkeleton label="Loading SMTP settings" /></main>;

  const savedForm = toForm(smtp);
  const dirty = Object.keys(form).some((key) => form[key] !== savedForm[key]);
  const activeSource = smtp?.activeSource || "unset";
  const status = {
    settings: { tone: "is-enabled", icon: "ti-circle-check-filled", label: "Active", text: `All emails are sent through ${smtp?.settings?.host}:${smtp?.settings?.port}.` },
    environment: { tone: "is-disabled", icon: "ti-server", label: "Server default", text: "Emails are sent with the server's MAIL_* environment settings. Turn on these settings to manage email delivery here." },
    unset: { tone: "is-unavailable", icon: "ti-alert-triangle", label: "Not configured", text: "No SMTP server is set up. Verification, password reset and invitation emails can't be sent." }
  }[activeSource];

  return <main className="workspace-dashboard admin-ai-settings admin-smtp">
    <header className="workspace-dashboard-header">
      <div>
        <span className="workspace-eyebrow">PLATFORM CONFIGURATION</span>
        <h1>SMTP configuration</h1>
        <p>Choose the mail server Socialyorix uses for every system email, including verification, password resets, invitations and welcome emails.</p>
      </div>
      <button className="admin-ai-secondary" type="button" onClick={loadSettings} disabled={loading || saving}>
        <i className="ti ti-refresh" aria-hidden="true" /> Refresh
      </button>
    </header>

    {pageError && <div className="admin-ai-error" role="alert">
      <i className="ti ti-alert-circle" aria-hidden="true" />
      <div><strong>Settings couldn’t be loaded</strong><p>{pageError}</p></div>
      <button className="admin-ai-secondary" type="button" onClick={loadSettings}>Try again</button>
    </div>}

    {!pageError && <div className="admin-smtp-layout">
      <form className="admin-ai-provider admin-smtp-card" onSubmit={saveSettings} noValidate aria-label="SMTP settings">
        <header className="admin-ai-provider-header">
          <div className="admin-ai-provider-icon admin-smtp-icon" aria-hidden="true"><i className="ti ti-mail-cog" /></div>
          <div className="admin-ai-provider-title">
            <h3>Mail server</h3>
            <p>{smtp?.updatedAt ? `Last saved ${formatDate(smtp.updatedAt)}` : "Not saved yet"}</p>
          </div>
          <span className={`admin-ai-status ${status.tone}`}><i className={`ti ${status.icon}`} aria-hidden="true" />{status.label}</span>
        </header>

        <fieldset disabled={!canManage || saving}>
          <legend>Server</legend>
          <div className="admin-smtp-grid is-server">
            <Field id="smtp-host" label="SMTP host" error={errors.host}>
              <input value={form.host} onChange={(event) => update("host", event.target.value)} placeholder="smtp.example.com" maxLength={253} autoComplete="off" spellCheck={false} />
            </Field>
            <Field id="smtp-port" label="Port" error={errors.port}>
              <input inputMode="numeric" value={form.port} onChange={(event) => update("port", event.target.value.replace(/[^\d]/g, ""))} placeholder="587" maxLength={5} />
            </Field>
          </div>
          <div className="admin-ai-credential-field admin-smtp-field admin-smtp-encryption-field">
            <span id="smtp-encryption-label">Encryption</span>
            <div className="admin-smtp-encryption" role="radiogroup" aria-labelledby="smtp-encryption-label" aria-describedby="smtp-encryption-hint">
              {encryptionOptions.map((option) => <label key={option.id} className={`admin-smtp-option${form.encryption === option.id ? " is-selected" : ""}`}>
                <input type="radio" name="smtp-encryption" value={option.id} checked={form.encryption === option.id} onChange={() => update("encryption", option.id)} />
                <span>{option.label}</span>
              </label>)}
            </div>
            <small id="smtp-encryption-hint" className="admin-smtp-field-hint">{encryptionOptions.find((option) => option.id === form.encryption)?.hint}</small>
          </div>
          {form.encryption === "none" && form.username.trim() && <div className="admin-ai-warning" role="note">
            <i className="ti ti-alert-triangle" aria-hidden="true" />
            <p>Without encryption, the username and password can be sent in plain text. Use TLS or SSL unless the server is on a trusted private network.</p>
          </div>}
        </fieldset>

        <fieldset disabled={!canManage || saving}>
          <legend>Authentication</legend>
          <div className="admin-smtp-grid">
            <Field id="smtp-username" label="Username" error={errors.username} hint="Leave blank if the server doesn't require sign-in.">
              <input value={form.username} onChange={(event) => update("username", event.target.value)} maxLength={320} autoComplete="off" spellCheck={false} />
            </Field>
            <Field id="smtp-password" label="Password" error={errors.password} hint={smtp?.passwordSaved ? "Saved securely. Leave blank to keep it." : "Stored encrypted. It's never shown again after saving."}>
              <input type="password" value={form.password} onChange={(event) => update("password", event.target.value)} maxLength={1024} autoComplete="new-password" placeholder={smtp?.passwordSaved ? "•••••••• (saved)" : "Enter password"} />
            </Field>
          </div>
        </fieldset>

        <fieldset disabled={!canManage || saving}>
          <legend>Sender</legend>
          <div className="admin-smtp-grid">
            <Field id="smtp-from-email" label="Sender email" error={errors.fromEmail} hint="Must be an address your SMTP server is allowed to send from.">
              <input type="email" value={form.fromEmail} onChange={(event) => update("fromEmail", event.target.value)} placeholder="no-reply@example.com" maxLength={254} autoComplete="off" />
            </Field>
            <Field id="smtp-from-name" label="Sender name" hint="Shown as the sender in the recipient's inbox.">
              <input value={form.fromName} onChange={(event) => update("fromName", event.target.value)} placeholder="Socialyorix" maxLength={100} />
            </Field>
          </div>
        </fieldset>

        <label className="admin-ai-toggle admin-smtp-toggle">
          <input type="checkbox" checked={form.enabled} disabled={!canManage || saving} onChange={(event) => update("enabled", event.target.checked)} />
          <span className="admin-ai-switch" aria-hidden="true" />
          <span>Use these settings for all outgoing email</span>
        </label>

        {canManage && <div className="admin-ai-provider-actions admin-smtp-actions">
          {dirty && <span className="admin-smtp-dirty"><i className="ti ti-point-filled" aria-hidden="true" />Unsaved changes</span>}
          <button className="admin-ai-secondary" type="button" onClick={() => { setForm(savedForm); setErrors({}); }} disabled={!dirty || saving}>Discard</button>
          <button className="admin-ai-primary" type="submit" disabled={saving || !dirty}>
            <i className={`ti ${saving ? "ti-loader-2 admin-smtp-spin" : "ti-device-floppy"}`} aria-hidden="true" />{saving ? "Saving…" : "Save settings"}
          </button>
        </div>}
      </form>

      <aside className="admin-smtp-side">
        <section className="admin-ai-provider admin-smtp-card" aria-labelledby="smtp-status-title">
          <h2 id="smtp-status-title">Delivery status</h2>
          <p className="admin-smtp-status-text">{status.text}</p>
          {smtp?.lastTest && <div className={`admin-smtp-last-test ${smtp.lastTest.status === "passed" ? "is-passed" : "is-failed"}`}>
            <i className={`ti ${smtp.lastTest.status === "passed" ? "ti-circle-check" : "ti-circle-x"}`} aria-hidden="true" />
            <div><strong>Last test {smtp.lastTest.status === "passed" ? "passed" : "failed"}</strong><small>{formatDate(smtp.lastTest.testedAt)}</small></div>
          </div>}
        </section>

        {canManage && <section className="admin-ai-provider admin-smtp-card" aria-labelledby="smtp-test-title">
          <h2 id="smtp-test-title">Send test email</h2>
          <p className="admin-smtp-status-text">Sends a real email using the settings in the form, so you can check them before saving.</p>
          <Field id="smtp-test-recipient" label="Recipient">
            <input type="email" value={recipient} onChange={(event) => { setRecipient(event.target.value); setTestResult(null); }} placeholder="you@example.com" maxLength={254} />
          </Field>
          <button className="admin-ai-secondary admin-smtp-test-button" type="button" onClick={sendTestEmail} disabled={testing || saving}>
            <i className={`ti ${testing ? "ti-loader-2 admin-smtp-spin" : "ti-send"}`} aria-hidden="true" />{testing ? "Sending…" : "Send test email"}
          </button>
          {testResult && <div className={`admin-smtp-result ${testResult.success ? "is-passed" : "is-failed"}`} role={testResult.success ? "status" : "alert"}>
            <i className={`ti ${testResult.success ? "ti-circle-check" : "ti-alert-circle"}`} aria-hidden="true" />
            <p>{testResult.message}</p>
          </div>}
        </section>}
      </aside>
    </div>}
  </main>;
};

export default SmtpSettings;
